import { Direction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import useSWR from "swr";

import usePassedCertifications from "./usePassedCertifications";
import usePassedCourses from "./usePassedCourses";
import useRecommendedCoursePlan from "./useRecommendedCoursePlan";
import fetcher from "~/utils/fetcher.client";

const useNextRecommended = (certificationSlug?: string) => {
	const { userId } = useAuth();

	const { isCoursePassed } = usePassedCourses();
	const { isCertificationPassed, isLoading } = usePassedCertifications();

	const { data: nextRecommended, ...rest } = useSWR<{
		courseId: string;
		planKey?: string;
		isResetAllowed: boolean;
	}>(
		userId &&
			certificationSlug &&
			!isCertificationPassed(certificationSlug) &&
			!isLoading &&
			`/certifications/${certificationSlug}/next-recommended`,
	);

	const { recommendedCoursePlan, isPlanItemPassed } = useRecommendedCoursePlan(
		certificationSlug === "courses" ? undefined : certificationSlug,
	);

	const planItemIndex = recommendedCoursePlan?.findIndex(
		(planItem) => planItem._key === nextRecommended?.planKey,
	);

	const planItem =
		planItemIndex !== undefined
			? recommendedCoursePlan?.[planItemIndex]
			: undefined;

	const courseIndex = planItem?.courses.findIndex(
		(course) => course._id === nextRecommended?.courseId,
	);

	const course =
		courseIndex !== undefined ? planItem?.courses[courseIndex] : undefined;

	const { mutate } = rest;

	const handleSkipCourse = async (id: string, direction: Direction) => {
		await mutate(
			fetcher(
				`/certifications/${certificationSlug}/next-recommended/skip-course`,
				{
					method: "POST",
					data: {
						id,
						direction,
					},
				},
			),

			{
				populateCache: false,
				revalidate: true,
				optimisticData: () => {
					const courseIndex = planItem?.courses.findIndex(
						(course) => course._id === id,
					)!;

					const nextCourse =
						direction === Direction.FORWARD
							? planItem?.courses.find(
									(course, index) =>
										!isCoursePassed(course._id) && index > courseIndex,
								)
							: planItem?.courses.findLast(
									(course, index) =>
										!isCoursePassed(course._id) && index < courseIndex,
								);

					return {
						...nextRecommended,
						courseId: nextCourse?._id!,
					} as NonNullable<typeof nextRecommended>;
				},
			},
		);
	};

	const handleReset = async () => {
		await mutate(
			await fetcher(
				`/certifications/${certificationSlug}/next-recommended/reset`,
				{
					method: "POST",
				},
			),
			{
				populateCache: false,
				revalidate: true,
			},
		);
	};

	const handleSkipPlanItem = async (key: string, direction: Direction) => {
		await mutate(
			fetcher(
				`/certifications/${certificationSlug}/next-recommended/skip-plan-item`,
				{
					method: "POST",
					data: {
						key,
						direction,
					},
				},
			),

			{
				populateCache: false,
				revalidate: true,

				optimisticData: () => {
					const planIndex = recommendedCoursePlan!.findIndex(
						(planItem) => planItem?._key === key,
					)!;

					const nextPlan =
						direction === Direction.FORWARD
							? recommendedCoursePlan!.find(
									(planItem, index) =>
										!isPlanItemPassed(planItem._key) && index > planIndex,
								)
							: recommendedCoursePlan!.findLast(
									(planItem, index) =>
										!isPlanItemPassed(planItem._key) && index < planIndex,
								);

					const nextCourse = nextPlan?.courses.find(
						(course) => !isCoursePassed(course._id),
					);

					return {
						...nextRecommended,
						courseId: nextCourse?._id,
						planKey: nextPlan?._key!,
					} as NonNullable<typeof nextRecommended>;
				},
			},
		);
	};

	return {
		handleSkipCourse,
		handleSkipPlanItem,
		handleReset,

		planItem,
		course,

		planItemIndex,
		courseIndex,
		isPlanItemPassed,
		nextRecommended,

		isLoadingNextRecommended: rest.isLoading,
		...rest,
	};
};

export default useNextRecommended;
